import { useState } from "react";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import api, { errorText } from "../../lib/api";
import { ago } from "../../lib/dash";
import { Tile, Chip } from "./Tile";

const STATUSES = ["idle", "printing", "error", "offline"];
const TONE = { idle: "green", printing: "blue", error: "red", offline: "amber" };

const PrinterRow = ({ p, onSaved }) => {
  const [status, setStatus] = useState("");                 // no default: a status is always a deliberate choice
  const [note, setNote] = useState(p.note || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dash/printers/${p.id}`, { status, note });
      onSaved(data.printers);
      toast.success(`${p.id} set to ${status}`);
    } catch (e) {
      toast.error(errorText(e, "Could not save the printer status."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li data-testid={`printer-${p.id}`} className="border-b border-border/60 py-2 last:border-0">
      <div className="flex items-center gap-2">
        <Printer className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="font-heading text-sm font-bold">{p.id}</span>
        <Chip testid={`printer-${p.id}-status`} tone={TONE[p.status]}>{STATUSES.includes(p.status) ? p.status : "Not set"}</Chip>
        <span className="ml-auto truncate text-[11px] text-muted-foreground">{p.updated_at ? `set ${ago(p.updated_at)}` : "never set"}</span>
      </div>
      <div className="mt-1.5 flex gap-1.5">
        <select data-testid={`printer-${p.id}-select`} value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-8 rounded-lg border border-input bg-background px-2 text-xs">
          <option value="" disabled>Choose…</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input data-testid={`printer-${p.id}-note`} value={note} maxLength={200} onChange={(e) => setNote(e.target.value)}
          placeholder="Note (optional)" className="h-8 min-w-0 flex-1 rounded-lg border border-input bg-background px-2 text-xs" />
        <button type="button" data-testid={`printer-${p.id}-save`} disabled={busy || !status} onClick={save}
          className="h-8 rounded-lg bg-foreground px-3 text-xs font-semibold text-background transition-opacity hover:opacity-85 disabled:opacity-50">
          Save
        </button>
      </div>
    </li>
  );
};

export const PrintersTile = ({ printers, onSaved, index }) => (
  <Tile id="printers" index={index} kicker="Tile 6 · not connected" title="Printers"
    action={<Chip testid="printers-source">Manual status</Chip>}>
    <ul className="dash-scroll min-h-0 flex-1 overflow-y-auto pr-1">
      {printers.map((p) => <PrinterRow key={`${p.id}-${p.updated_at}`} p={p} onSaved={onSaved} />)}
    </ul>
    <p className="mt-1 text-[11px] text-muted-foreground">Set by hand and audited. The Bambu server will report here later.</p>
  </Tile>
);
